// Date and Time Utility Functions
// Standardizes date/time handling across the application

// Default timezone for the application (IST - Indian Standard Time)
const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/**
 * Convert a date to IST timezone
 * @param date - Date to convert
 * @returns Date in IST timezone
 */
export function toIST(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Create a new date object in IST
  const istDate = new Date(dateObj.toLocaleString('en-US', {
    timeZone: DEFAULT_TIMEZONE
  }));
  
  return istDate;
}

/**
 * Format date for display (DD/MM/YYYY)
 * @param date - Date to format
 * @returns Formatted date string
 */
export function formatDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    timeZone: DEFAULT_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

/**
 * Format time for display (12-hour format with AM/PM)
 * @param date - Date to format
 * @returns Formatted time string
 */
export function formatTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleTimeString('en-US', {
    timeZone: DEFAULT_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Format date and time for display (DD/MM/YYYY, HH:MM AM/PM)
 * @param date - Date to format
 * @returns Formatted date and time string
 */
/** A real, readable date — not missing, not "Invalid Date". */
function isUsableDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

/**
 * The 24-hour HH:MM an <input type="time"> requires.
 *
 * Distinct from formatTime, which is for reading — that returns "10:00 am",
 * which a time input cannot display and arithmetic cannot parse.
 */
export function formatTimeForInput(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!isUsableDate(dateObj)) return '';
  return dateObj.toLocaleTimeString('en-GB', {
    timeZone: DEFAULT_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

export function formatDateTime(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!isUsableDate(dateObj)) return '';
  // en-IN, not en-US: this renders day before month, which is what everyone
  // here reads and — more importantly — what the date picker parses back when
  // somebody types a date in. With the American order, 5 September displayed as
  // 09/05 and was read back as 9 May.
  return dateObj.toLocaleString('en-IN', {
    timeZone: DEFAULT_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Format date for datetime-local input (YYYY-MM-DDTHH:MM)
 * @param date - Date to format
 * @returns Formatted date string for input fields
 */
export function formatDateTimeLocal(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  // A record with no date, or an unreadable one, produced "NaN-NaN-NaNTNaN:NaN",
  // which then read back as an invalid date and filled the calendar with NaN.
  // Nothing is a better answer than nonsense: the field shows its placeholder
  // and the calendar opens on today, so a date can actually be chosen.
  if (!isUsableDate(dateObj)) return '';

  // Convert to IST first
  const istDate = toIST(dateObj);
  
  const year = istDate.getFullYear();
  const month = String(istDate.getMonth() + 1).padStart(2, '0');
  const day = String(istDate.getDate()).padStart(2, '0');
  const hours = String(istDate.getHours()).padStart(2, '0');
  const minutes = String(istDate.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Parse a datetime-local input value to a Date object in IST
 * @param dateTimeLocal - String from datetime-local input
 * @returns Date object in IST
 */
export function parseDateTimeLocal(dateTimeLocal: string): Date {
  if (!dateTimeLocal) return new Date();
  
  // Create date from the input (browser handles timezone conversion)
  const date = new Date(dateTimeLocal);
  
  // Convert to IST
  return toIST(date);
}

/**
 * Format date for calendar display (e.g., "Monday, January 1, 2024")
 * @param date - Date to format
 * @returns Formatted date string
 */
export function formatDateLong(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    timeZone: DEFAULT_TIMEZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Format date for short display (e.g., "Jan 1, 2024")
 * @param date - Date to format
 * @returns Formatted date string
 */
export function formatDateShort(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    timeZone: DEFAULT_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Get timezone offset string (e.g., "+05:30" for IST)
 * @returns Timezone offset string
 */
export function getTimezoneOffset(): string {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  const hours = Math.abs(Math.floor(offset / 60));
  const minutes = Math.abs(offset % 60);
  const sign = offset <= 0 ? '+' : '-';
  
  return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}

/**
 * Get current timezone name
 * @returns Timezone name
 */
export function getCurrentTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Check if a date is in the past
 * @param date - Date to check
 * @returns True if date is in the past
 */
export function isPast(date: Date | string): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj < new Date();
}

/**
 * Check if a date is in the future
 * @param date - Date to check
 * @returns True if date is in the future
 */
export function isFuture(date: Date | string): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj > new Date();
}

/**
 * Get time remaining until a date
 * @param date - Target date
 * @returns Time remaining string
 */
export function getTimeRemaining(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = dateObj.getTime() - now.getTime();
  
  if (diff <= 0) return 'Time has passed';
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} left`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} left`;
  return `${minutes} minute${minutes > 1 ? 's' : ''} left`;
}
